import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import PageWrapper from 'Pages/@PageWrapper'
import { AppFilterPanel, AppPageTabs, AppInfoButton } from '@components'
import { dsColorNew } from '@palette'
import { setupScopeSx } from 'Pages/DsRepricing/Setup/@components/setupScopeSx'
import { DS_REPRICING_ROUTES } from '@constants/routes'

import CategoryConsistencyTab from 'Pages/DsRepricing/Setup/CategoryConsistency'
import PriceLimitsTab         from 'Pages/DsRepricing/Setup/PriceLimits'
import PriceMatricesTab       from 'Pages/DsRepricing/Setup/PriceMatrices'
import ModeSwitch             from 'Pages/DsRepricing/Setup/@components/ModeSwitch'
import QuarantineCard         from 'Pages/DsRepricing/Setup/@components/QuarantineCard'
import ProductsTab            from 'Pages/DsRepricing/Setup/Products'
import TetheringTab           from 'Pages/DsRepricing/Setup/Tethering'
import { PricelistCatalogProvider, AxisCatalogProvider } from 'Pages/DsRepricing/Setup/@services'
import MigrationBanner from 'Pages/DsRepricing/@components/MigrationBanner'

/**
 * Repricing General Setup — envelope that constrains every Strategy.
 * Tabs persist through webback (`/api/repricing/setup/*`) by default, or
 * through the Phase-1 localStorage mocks when `dsr_setup:use_mocks` is set
 * (see `@services/flag.js`). Every Surface bypasses the global grey-Paper
 * override to keep contrast readable.
 */
//: Two groups, named at last. The order was already 3 + 3 and
//: contiguous, defended by the two comments below, but nothing on
//: screen said what the halves were (Curro, 2026-10-03: "el setup
//: empieza a ser un monstruo"). Labels only — a second level of
//: navigation would cost a click on every visit and only starts paying
//: past eight or nine leaves.
const GROUPS = [
	{ key: 'sets',   label: 'What sets a floor',
	  keys: ['category-consistency', 'price-matrix', 'price-limits'] },
	// Rate cards → Products (CU-869fhuhy6, brief v3: one offer, configured
	// per site). No "When it runs" tab: the cadence is always the
	// strategy's own. The old Rate cards tab stays reachable read-only
	// until the migration is validated (UX D23).
	{ key: 'engine', label: 'What the engine does with it',
	  keys: ['products', 'tethering', 'quarantine'] },
]

const TABS = [
	{ key: 'category-consistency', label: 'Category consistency', Component: CategoryConsistencyTab },
	// Before Price limits, because it is written first: a matrix has no
	// scope of its own, and the tab that binds one to a scope is the
	// next one along (Curro, 2026-10-01).
	{ key: 'price-matrix',         label: 'Price matrix',         Component: PriceMatricesTab },
	{ key: 'price-limits',         label: 'Price limits',         Component: PriceLimitsTab },
	{ key: 'products',             label: 'Products',             Component: ProductsTab },
	{ key: 'tethering',            label: 'Tethering',            Component: TetheringTab },
	// Last: the last net before a price is sent. A tab and not a header
	// card because the header is for what governs every tab (Curro, 2026-10-01).
	{ key: 'quarantine',           label: 'Quarantine',           Component: QuarantineCard },
]

export default function DsRepricingSetupPage() {
	const [searchParams, setSearchParams] = useSearchParams()

	const initialTab = useMemo(() => {
		const t = searchParams.get('tab')
		// Old deep links of the first cut of this task land on Products.
		if (t === 'tariff-links' || t === 'when-it-runs') return 'products'
		return TABS.some(x => x.key === t) ? t : TABS[0].key
	}, [])
	const [activeKey, setActiveKey] = useState(initialTab)
	// A link inside the page (Price limits → `?tab=price-matrix&matrix=<id>`,
	// CU-869fkbjdh) changes the search params without remounting: the tab
	// follows them. `setTab` writes both at once, so this never loops.
	useEffect(() => {
		const t = searchParams.get('tab')
		if (t && t !== activeKey && TABS.some(x => x.key === t)) setActiveKey(t)
	}, [searchParams])
	const setTab = (key) => {
		setActiveKey(key)
		const next = new URLSearchParams(searchParams)
		next.set('tab', key)
		setSearchParams(next, { replace: true })
	}

	const active = TABS.find(t => t.key === activeKey) || TABS[0]
	const Body = active.Component

	return (
		<PricelistCatalogProvider>
		{/* Price limits (and the wizard's card 3) read the three axes; the
		    catalogue is fetched once here rather than per tab. */}
		<AxisCatalogProvider>
		<PageWrapper
			title={DS_REPRICING_ROUTES.SETUP_TITLE}
			// What Setup is, behind an ⓘ beside the subtitle (it opened the page as a paragraph)
			subtitle={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
				{DS_REPRICING_ROUTES.SETUP_SUBTITLE}
				<AppInfoButton title={DS_REPRICING_ROUTES.SETUP_TITLE} size={18}>
					The envelope every Strategy runs inside. Anything defined here
					is a hard guardrail — Strategies can never violate it. Use
					Category consistency and Price limits to draw the safety net,
					Products to say, per site, which pricelist each of our offers
					reads and writes and how far a broker may mark it up, and the
					Activity to review what the net catches. When a strategy runs
					is the strategy's own, in its wizard.
				</AppInfoButton>
			</Box>}
		>
			<MigrationBanner where="setup"/>

			{/* The page's white band, as My Business Admin: the mode that governs every tab, and the
			    tabs in their two groups. */}
			<AppFilterPanel>
				{/* Above the tabs on purpose: it governs every one of them. */}
				<ModeSwitch/>

				<Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3, flexWrap: 'wrap' }}>
					{GROUPS.map(g => (
						<Box key={g.key} sx={{ minWidth: 0 }}>
							{/* The label sits over its own strip rather than over a
							    slice of one, so it lines up by construction. */}
							<Typography sx={{ mb: 0.75, fontSize: 11, fontWeight: 600, letterSpacing: 0.6,
							                  textTransform: 'uppercase', color: dsColorNew.textSubtle,
							                  whiteSpace: 'nowrap' }}>
								{g.label}
							</Typography>
						<AppPageTabs value={g.keys.includes(activeKey) ? activeKey : false}
						             onChange={(_, k) => setTab(k)}
						             stackProps={{ sx: { mb: 0 } }}
						             sx={{ '& .MuiTabs-flexContainer > .MuiTab-root': { mr: 0 } }}
						             tabs={TABS.filter(t => g.keys.includes(t.key))
						                       .map(t => ({
						                       	key: t.key, value: t.key,
						                       	label: t.label,
						                       }))}/>
						</Box>
					))}
				</Box>
			</AppFilterPanel>

			<Box sx={setupScopeSx}>
				<Body/>
			</Box>
		</PageWrapper>
		</AxisCatalogProvider>
		</PricelistCatalogProvider>
	)
}
